import React from 'react';
import { Switch as MaterialUiSwitch, switchClasses, SwitchProps } from '@mui/material';
import { ISharedProps } from '../../tools/ISharedProps';
import { ISharedFormProps } from '../ISharedFormProps';
import { makeStyles } from '~/UIKit/Style';
import { FONT_SIZE_SMALLISH } from '~/Constants/UI.constants';
import { CHARCOAL_GREY, PRIMARY_BLUE, GRAY_LIGHT } from '~/Constants/Colors.constants';

export interface ISwitcherProps extends SwitchProps, ISharedProps, ISharedFormProps {
  placeholder?: string;
  secondaryPlaceholder?: string;
  width?: number;
  small?: boolean;
}

const useStyles = ({
  placeholder = '',
  secondaryPlaceholder = '',
  checked,
  width = 53,
  small,
}: ISwitcherProps) => {
  const THUMB_BORDER_RADIUS = 14;
  const THUMB_FONT_SIZE = FONT_SIZE_SMALLISH;

  const switchBasePadding = small ? 1 : 0.5;
  const widthSize = small ? 34 : width;
  const thumbWidth = widthSize / 2 + 2;
  const translate = small ? 14 : widthSize - thumbWidth - 3;

  return makeStyles({
    root: {
      width: widthSize,
      height: small ? 20 : 32,
      padding: 0,
      color: CHARCOAL_GREY,
      overflow: 'hidden',
      borderRadius: widthSize / 3,
    },
    switchBase: {
      padding: switchBasePadding,
      borderRadius: widthSize / 3,
      [`& .${switchClasses['checked']}`]: {
        '& + $track': {
          backgroundColor: PRIMARY_BLUE,
        },
      },
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.01)',
      },
      '& .MuiTouchRipple-child': {
        transform: 'scale(1.2)',
      },
    },
    thumb: {
      color: 'white',
      width: small ? 18 : thumbWidth,
      height: small ? 18 : 29,
      margin: small ? 0 : 1,
      fontSize: THUMB_FONT_SIZE,
      position: 'relative',
      border: '0.5px solid rgba(0,0,0,0.04)',
      borderRadius: THUMB_BORDER_RADIUS,
      boxShadow: '0 3px 8px 0 rgba(0,0,0,0.12), 0 3px 1px 0 rgba(0,0,0,0.04)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&:after': {
        content: `"${checked ? secondaryPlaceholder : placeholder}"`,
        color: CHARCOAL_GREY,
      },
    },
    track: {
      color: CHARCOAL_GREY,
      backgroundColor: GRAY_LIGHT,
      opacity: '1 !important',
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      textAlign: 'center',
      alignItems: 'center',
      '&:after, &:before': {
        color: CHARCOAL_GREY,
        fontSize: THUMB_FONT_SIZE,
      },
      '&:after': {
        content: `"${secondaryPlaceholder}"`,
      },
      '&:before': {
        content: `"${placeholder}"`,
        color: 'white',
      },
    },
    checked: {
      transform: `translateX(${translate}px) !important`,
    },
  })();
};

export const Switcher = ({
  checked = false,
  placeholder,
  secondaryPlaceholder,
  width,
  small,
  ...props
}: ISwitcherProps) => {
  const { classes } = useStyles({ placeholder, secondaryPlaceholder, width, checked, small });

  return (
    <MaterialUiSwitch
      classes={{
        root: classes.root,
        switchBase: classes.switchBase,
        thumb: classes.thumb,
        track: classes.track,
        checked: classes.checked,
      }}
      color="primary"
      name={props.field?.name}
      onChange={props.field?.onChange}
      onBlur={props.field?.onBlur}
      checked={(props.field?.value || checked) ?? false}
      {...props}
    />
  );
};
